<!-- eslint-disable vue/no-use-v-if-with-v-for -->
<template>
  <div :class="b(modifiers)">
    <template v-if="$viewport.isMd || !showAsCollapseOnMobile">
      <div
        v-if="tabs.length"
        :class="b('head')"
      >
        <ul
          :class="b('list')"
          :style="listStyles"
          role="tablist"
          @mouseleave="onListMouseLeave"
        >
          <li
            v-for="tab in tabs"
            :key="tab.tabId"
            :class="b('list-item')"
          >
            <span
              v-if="tab.slug"
              :id="tab.slug"
              :class="b('anchor')"
            ></span>
            <component
              :is="tab.link ? 'a' : 'button'"
              :id="tab.tabId"
              ref="tabs"
              :class="b('tab', { isActive: tab.isActive, isSubtle: tab.isSubtle })"
              :aria-controls="!tab.link ? tab.tabPanelId : null"
              :aria-selected="!tab.link && tab.isActive ? tab.isActive : null"
              :tabindex="tab.isActive ? 0 : -1"
              :role="!tab.link && 'tab'"
              :href="tab.link && tab.link.href"
              :target="tab.link && tab.link.target"
              @click="!tab.link && onButtonClick(tab)"
              @mouseover="updateActiveStateStyles(tab)"
            >
              {{ tab.name }}
            </component>
          </li>
        </ul>
        <a
          v-if="link"
          :class="b('link')"
          :href="link.url"
        >
          {{ link.title }}
          <e-icon-sprite
            icon="i-chevron-2--right"
            size="15"
          />
        </a>
      </div>
      <slot></slot>
    </template>
    <template v-else>
      <c-collapse-group>
        <slot></slot>
      </c-collapse-group>
      <a
        v-if="tabs.length && link && link.url"
        :class="b('link')"
        :href="link.url"
        :target="link.target"
      >
        {{ link.title }}
        <e-icon-sprite
          icon="i-chevron-2--right"
          size="15"
        />
      </a>
    </template>
  </div>
</template>

<script>
  import { provide, reactive } from 'vue';
  import cCollapseGroup from '@/components/c-collapse-group.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import url from '@/mixins/url.js';

  /**
   * Renders a list of tabs.
   *
   * TODO: storing the active state on the child items is not good practice and should be refactored.
   */
  export default {
    name: 'c-tabs',

    components: {
      eIconSprite,
      cCollapseGroup,
    },

    mixins: [url],

    props: {
      /**
       * Defines if tabs should be displayed as collapse elements on mobile viewports
       */
      showAsCollapseOnMobile: {
        type: Boolean,
        default: true,
      },

      /**
       * Expects an optional additional link.
       *
       * @property {Object} link
       * @property {String} link.url - URL.
       * @property {String} link.target - Link target.
       * @property {String} link.title - Title/label of the link.
       */
      link: {
        type: Object,
        default: null,
      },

      /**
       * Defines the style of the tab list.
       */
      listWidth: {
        type: String,
        default: '100',
        validator(value) {
          return ['100', 'short'].includes(value);
        },
      },

      /**
       * Defines the style of the tab list.
       */
      listAlign: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'right'].includes(value);
        },
      },
    },

    setup() {
      const tabsProvider = reactive({
        activeTabId: null,
        tabs: [],
      });

      provide('TabsProvider', tabsProvider);

      return {
        tabsProvider,
      };
    },

    data() {
      return {
        /**
         * @type {Object} Stores the style definition for the list element.
         */
        listStyles: null,
      };
    },

    computed: {
      tabs() {
        return this.tabsProvider?.tabs || [];
      },

      /**
       * Returns all modifiers for the component main class.
       *
       * @returns {Object}
       */
      modifiers() {
        return {
          listWidth: this.listWidth,
          listAlign: this.listAlign,
        };
      },
    },
    watch: {
      '$viewport.isMd': {
        handler() {
          this.tabsProvider.tabs = [];
        },
      },

      'showAsCollapseOnMobile': {
        handler() {
          this.tabsProvider.tabs = [];
        },
      },

      'tabs': {
        deep: true,
        handler() {
          const activeTab = this.tabs.find((tab) => tab.isActive);

          this.updateActiveStateStyles(activeTab);
        },
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      if (this.hasHash()) {
        this.handleTabThroughHash();
      } else {
        const activeTabs = this.tabs?.filter((tab) => tab.isActive);

        if (activeTabs.length > 1) {
          activeTabs.slice(1).forEach((tab) => {
            tab.isActive = false; // Prevents multiple tabs being active.
          });
        }

        if (activeTabs[0]) {
          this.updateActiveStateStyles(activeTabs[0]);
        }
      }

      window.addEventListener('resizeend', this.onResizeEnd);
      window.addEventListener('hashchange', this.handleTabThroughHash);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('hashchange', this.handleTabThroughHash);
      window.removeEventListener('resizeend', this.onResizeEnd);
    },
    // unmounted() {},

    methods: {
      /**
       * Resize event handler.
       */
      onResizeEnd() {
        this.$nextTick(() => {
          const activeTab = this.tabs.find((tab) => tab.isActive);

          this.updateActiveStateStyles(activeTab);
        });
      },

      /**
       * Mouseout event handler for the tab list.
       */
      onListMouseLeave() {
        const activeTab = this.tabs.find((tab) => tab.isActive);

        this.updateActiveStateStyles(activeTab);
      },

      /**
       * Sets tab active.
       *
       * @param {Object} activeTab - The given tab.
       */
      onButtonClick(activeTab) {
        let active;

        this.tabs.forEach((tab) => {
          tab.isActive = tab.tabId === activeTab.tabId;

          if (tab.isActive) {
            this.tabsProvider.activeTabId = tab.tabId;

            active = tab;
          }
        });

        this.updateActiveStateStyles(active);
      },

      /**
       * Updates the active state styles.
       *
       * @param {Object} tab - An element of type Vue component c-tab-panel.
       */
      updateActiveStateStyles(tab) {
        this.$nextTick(() => {
          const tabButton = tab ? this.$refs.tabs?.find((tabItem) => tabItem.id === tab.tabId) : null;

          this.listStyles = {
            '--c-tabs--active-start': `${tabButton?.offsetLeft || 0}px`,
            '--c-tabs--active-width': `${tabButton?.offsetWidth || 0}px`,
          };
        });
      },

      /*
       * Find tab through hash and set it to active
       */
      handleTabThroughHash() {
        const urlHash = this.getHashValue();
        const tabThroughHash = this.tabs?.find((tab) => tab.slug === urlHash);

        if (tabThroughHash) {
          this.onButtonClick(tabThroughHash);
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-tabs {
    $this: &;

    $border: 1px;

    @extend %component-spacing;

    &__head {
      display: flex;
      align-items: center;
    }

    &__list {
      @extend %list-reset;

      position: relative;
      display: flex;
      flex: 1;
      border-bottom: $border solid variables.$color-grayscale--900;

      &::after {
        position: absolute;
        bottom: -$border;
        left: var(--c-tabs--active-start, 0);
        display: block;
        content: '';
        width: var(--c-tabs--active-width, 0);
        height: 0;
        border-bottom: 2px solid variables.$color-primary--2;
        transition:
          left variables.$transition-duration--300 ease-in-out,
          width variables.$transition-duration--300 ease-in-out;
      }
    }

    &__tab {
      @extend %button-reset;
      @include mixins.font(variables.$font-size--18, 24px);

      position: relative;
      display: block;
      margin: functions.rem(0 variables.$spacing--25);
      padding: functions.rem(variables.$spacing--15 variables.$spacing--10);
      color: variables.$color-primary--1;
      transition: color variables.$transition-duration--200 ease-in-out;

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }

      &:hover,
      &--is-active {
        color: variables.$color-primary--2;
      }

      &--is-subtle,
      &--is-subtle:hover {
        color: variables.$color-grayscale--500;
      }

      &--is-active {
        #{$this}_list::after {
          // Legacy support
          right: 0;
          bottom: -1px; // Compensate separator
          left: 0;
          content: '';
          opacity: 1;
          border-bottom: 2px solid variables.$color-primary--2;
          transition: opacity variables.$transition-duration--300 ease-in-out;

          @supports (--css: variables) {
            // Remove in modern browsers
            content: none;
          }
        }
      }
    }

    &__list-item:first-child &__tab {
      margin-left: 0;
    }

    &__list-item:last-child &__tab {
      margin-right: 0;
    }

    &__anchor {
      position: absolute;
      top: calc(var(--header-height) * -1);
      right: 0;
      left: 0;
      visibility: hidden;
    }

    &__link {
      @extend %link--gray;
      @include mixins.font(variables.$font-size--18, 24px, variables.$font-weight--semi-bold);

      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: functions.rem(variables.$spacing--15 0);

      @include mixins.media(md) {
        padding: functions.rem(
          variables.$spacing--15 variables.$spacing--10 variables.$spacing--15 variables.$spacing--30
        );
        border-bottom: 1px solid transparent;
      }

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }

      .e-icon-sprite {
        @include mixins.showMoreIcon();

        margin-top: 0.1em;
      }
    }

    &--list-width-short &__list {
      flex: 0 0 auto;
      padding: functions.rem(0 variables.$spacing--10);
    }

    &--list-align-right &__head,
    &--list-align-right &__list {
      justify-content: flex-end;
    }
  }
</style>
